import { IMdfStrategicInitiativeAllocation } from '../model/types'

import { normalizeSpaces } from './normalizeSpaces'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const toText = (value: unknown): string =>
  typeof value === 'string' ? normalizeSpaces(value).trim() : ''

/** Numbers and numeric strings pass through; `null`, `NaN`, `Infinity` and junk become `0`. */
const toNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)

    return Number.isFinite(parsed) ? parsed : 0
  }

  return 0
}

const mapAllocation = (raw: Record<string, unknown>): IMdfStrategicInitiativeAllocation => ({
  businessUnit: toText(raw.businessUnit),
  category: toText(raw.category),
  strategicInitiative: toText(raw.strategicInitiative),
  businessFocus: toText(raw.businessFocus),
  categoryAllocationLc: toNumber(raw.categoryAllocationLC),
  categoryAllocationPercent: toNumber(raw.categoryAllocationPercent),
})

/** An entry with no business unit, category and initiative can't be grouped or labelled — drop it. */
const hasIdentity = ({
  businessUnit,
  category,
  strategicInitiative,
}: IMdfStrategicInitiativeAllocation): boolean =>
  Boolean(businessUnit || category || strategicInitiative)

/**
 * Unpacks the activity's category split from the JSON-encoded `StrategicInitiatives`
 * field. Never throws: `null`, empty, malformed JSON or a non-array payload all map
 * to `[]`, non-object entries are skipped and individual fields are coerced, so a
 * partially broken payload still yields the entries that are usable.
 */
/** Unwraps however the split arrives: the JSON string both endpoints document, an array already
 *  parsed for us, or a string that still holds a JSON string after a double encode. Anything
 *  else — and any parse failure — reads as "no split". */
const toAllocationArray = (raw: unknown, depth = 0): unknown[] => {
  if (Array.isArray(raw)) {
    return raw
  }

  if (typeof raw !== 'string' || !raw || depth > 1) {
    return []
  }

  try {
    return toAllocationArray(JSON.parse(raw), depth + 1)
  } catch {
    return []
  }
}

export const parseStrategicInitiativeAllocations = (
  raw: string | unknown[] | null | undefined
): IMdfStrategicInitiativeAllocation[] =>
  toAllocationArray(raw).filter(isRecord).map(mapAllocation).filter(hasIdentity)
